import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, DollarSign, User, Settings as SettingsIcon, Trash2, ExternalLink, X } from "lucide-react";
import { Avatar, Badge, Button, ColorSwatchPicker, Input, KanbanSkeleton, Label, Modal, ModalFooter, Select, Skeleton, useConfirm, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { fullName, formatMoney, formatRelative } from "@/lib/format";
import { SmartListPicker } from "@/features/lists/SmartListPicker";
import { useSmartList, smartListQueryParams } from "@/features/lists/SmartListContext";
import type { Contact, Opportunity, Pipeline, PipelineStage } from "@/lib/types";

export function PipelinesPage() {
  const toast = useToast();
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [opps, setOpps] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingOpp, setEditingOpp] = useState<Opportunity | null>(null);
  const [pipelineDialog, setPipelineDialog] = useState<Pipeline | "new" | null>(null);
  const { listId, filter: smartFilter, set: setSmartList } = useSmartList();
  const scrollRef = useRef<HTMLDivElement>(null);

  const active = pipelines.find((p) => p.id === activeId) ?? null;

  const loadPipelines = async () => {
    try {
      const d = await api.get<{ pipelines: Pipeline[] }>("/pipelines");
      setPipelines(d.pipelines);
      if (!activeId && d.pipelines.length > 0) setActiveId(d.pipelines[0]!.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load pipelines");
    }
  };
  const loadOpps = async (pid: string) => {
    setLoading(true);
    try {
      const d = await api.get<{ opportunities: Opportunity[] }>("/opportunities", {
        pipeline_id: pid,
        ...smartListQueryParams(listId, smartFilter),
      });
      setOpps(d.opportunities);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load opportunities");
    } finally { setLoading(false); }
  };

  useEffect(() => { void loadPipelines(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { if (activeId) void loadOpps(activeId); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeId, listId, smartFilter]);

  // Click-and-drag horizontal scroll on the kanban board. Triggers when the
  // user clicks empty space (not a card, button, input) and drags. Coexists
  // with the existing card-drag-between-stages behavior — cards have their
  // own draggable=true so the early-return below skips them.
  //
  // Re-runs whenever the active pipeline changes, since the scrollable div
  // is conditionally rendered (only mounts after pipelines load).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let isDown = false;
    let startX = 0;
    let scrollStart = 0;

    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Skip when the user is interacting with a card/button/input/link.
      if (target.closest("[draggable=true], button, input, select, textarea, a")) return;
      isDown = true;
      startX = e.pageX;
      scrollStart = el.scrollLeft;
      el.style.cursor = "grabbing";
      // Prevent text-selection drag artifacts.
      e.preventDefault();
    };
    const onMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const dx = e.pageX - startX;
      el.scrollLeft = scrollStart - dx;
    };
    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      el.style.cursor = "";
    };

    el.addEventListener("mousedown", onDown);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("mouseleave", onUp);
    return () => {
      el.removeEventListener("mousedown", onDown);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("mouseleave", onUp);
    };
  }, [active?.id]);

  const oppsByStage = useMemo(() => {
    const map = new Map<string, Opportunity[]>();
    for (const o of opps) {
      const list = map.get(o.stage_id) ?? [];
      list.push(o);
      map.set(o.stage_id, list);
    }
    return map;
  }, [opps]);

  const totals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const o of opps) {
      if (o.status === "open") {
        totals.set(o.stage_id, (totals.get(o.stage_id) ?? 0) + (o.value_cents ?? 0));
      }
    }
    return totals;
  }, [opps]);

  const onDrop = async (oppId: string, stageId: string) => {
    setOpps((prev) => prev.map((o) => o.id === oppId ? { ...o, stage_id: stageId } : o));
    try {
      await api.patch(`/opportunities/${oppId}`, { stage_id: stageId });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to move");
      if (activeId) loadOpps(activeId);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Sticky toolbar */}
      <div className="flex flex-col gap-3 border-b border-zinc-800/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold text-zinc-100 sm:text-xl">{active?.name ?? "Pipelines"}</h1>
          <Select value={activeId ?? ""} onChange={(e) => setActiveId(e.target.value)} className="w-44 h-8 text-xs">
            {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <span className="hidden sm:inline text-xs text-zinc-500">{opps.length} {opps.length === 1 ? "deal" : "deals"}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" leftIcon={<SettingsIcon className="h-3.5 w-3.5" />} onClick={() => setPipelineDialog(active)} disabled={!active}>
            <span className="hidden sm:inline">Edit pipeline</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPipelineDialog("new")}>
            <Plus className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">New pipeline</span>
          </Button>
          <SmartListPicker
            listId={listId}
            filter={smartFilter}
            onChange={(lid, f) => setSmartList(lid, f)}
          />
          <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)} disabled={!active}>
            New deal
          </Button>
        </div>
      </div>

      {/* Kanban — fills remaining height; horizontal scroll lives inside */}
      {!active ? (
        <div className="flex-1 overflow-hidden"><KanbanSkeleton /></div>
      ) : (
        <div ref={scrollRef} className="flex-1 overflow-x-auto overflow-y-hidden cursor-grab">
          <div className="inline-flex h-full min-w-full gap-3 p-4 sm:p-6">
            {active.stages.map((stage) => {
              const stageOpps = oppsByStage.get(stage.id) ?? [];
              const total = totals.get(stage.id) ?? 0;
              return (
                <div
                  key={stage.id}
                  className="flex h-full w-72 shrink-0 flex-col rounded-xl border border-zinc-800/70 bg-zinc-900/30"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    const id = e.dataTransfer.getData("text/opp-id");
                    if (id) onDrop(id, stage.id);
                  }}
                >
                  <div className="flex items-center justify-between border-b border-zinc-800/70 px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <Badge color={stage.color || "zinc"} size="sm">{stage.name}</Badge>
                      <span className="text-xs text-zinc-500">{stageOpps.length}</span>
                    </div>
                    {total > 0 && <span className="text-xs font-medium text-zinc-400">{formatMoney(total)}</span>}
                  </div>
                  <div className="flex-1 space-y-2 overflow-y-auto p-2 min-h-0">
                    {loading ? (
                      <>
                        <Skeleton className="h-20" />
                        <Skeleton className="h-16" />
                        <Skeleton className="h-20" />
                      </>
                    ) : stageOpps.length === 0 ? (
                      <p className="text-xs text-zinc-600 text-center py-4">No deals</p>
                    ) : stageOpps.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData("text/opp-id", o.id)}
                        onClick={() => setEditingOpp(o)}
                        className="block w-full cursor-grab rounded-lg border border-zinc-800 bg-zinc-950 p-3 text-left hover:border-zinc-700 active:cursor-grabbing"
                      >
                        <p className="text-sm font-medium text-zinc-100 truncate">{o.title}</p>
                        {o.contact_name && <p className="mt-1 truncate text-xs text-zinc-500">{o.contact_name}</p>}
                        <div className="mt-2 flex items-center justify-between">
                          {o.value_cents > 0 ? (
                            <span className="text-xs font-semibold text-emerald-400">{formatMoney(o.value_cents, o.currency ?? "USD")}</span>
                          ) : (
                            <span className="text-xs text-zinc-600">—</span>
                          )}
                          <span className="text-[10px] text-zinc-500">{formatRelative(o.updated_at)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <OpportunityDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        pipeline={active}
        onSaved={() => { setCreateOpen(false); if (activeId) loadOpps(activeId); }}
      />
      {editingOpp && (
        <OpportunityDialog
          open={!!editingOpp}
          onClose={() => setEditingOpp(null)}
          pipeline={active}
          opportunity={editingOpp}
          onSaved={() => { setEditingOpp(null); if (activeId) loadOpps(activeId); }}
        />
      )}
      {pipelineDialog && (
        <PipelineEditDialog
          pipeline={pipelineDialog === "new" ? null : pipelineDialog}
          onClose={() => setPipelineDialog(null)}
          onSaved={async (newId) => {
            setPipelineDialog(null);
            await loadPipelines();
            if (newId) setActiveId(newId);
          }}
        />
      )}
    </div>
  );
}

// ─── Pipeline edit dialog ──────────────────────────────────────────────────
function PipelineEditDialog({
  pipeline, onClose, onSaved,
}: {
  pipeline: Pipeline | null;
  onClose: () => void;
  onSaved: (newId?: string) => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [name, setName] = useState(pipeline?.name ?? "");
  const [stages, setStages] = useState<PipelineStage[]>(
    pipeline?.stages ?? [
      { id: "new",        name: "New",        order: 0, color: "zinc" },
      { id: "contacted",  name: "Contacted",  order: 1, color: "blue" },
      { id: "qualified",  name: "Qualified",  order: 2, color: "indigo" },
      { id: "won",        name: "Won",        order: 3, color: "emerald", probability: 100, win_stage: true },
      { id: "lost",       name: "Lost",       order: 4, color: "rose",    probability: 0,   lose_stage: true },
    ],
  );
  const [saving, setSaving] = useState(false);

  const updateStage = (idx: number, patch: Partial<PipelineStage>) => {
    setStages((prev) => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  };
  const moveStage = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= stages.length) return;
    const next = stages.slice();
    [next[idx], next[j]] = [next[j]!, next[idx]!];
    setStages(next.map((s, i) => ({ ...s, order: i })));
  };
  const addStage = () => {
    const id = `stage_${Date.now()}`;
    setStages([...stages, { id, name: "New stage", order: stages.length, color: "zinc" }]);
  };
  const removeStage = (idx: number) => {
    if (stages.length <= 2) {
      toast.error("A pipeline needs at least 2 stages");
      return;
    }
    setStages((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i })));
  };

  const submit = async () => {
    if (!name.trim()) return;
    if (stages.some((s) => !s.name.trim())) {
      toast.error("All stages need a name");
      return;
    }
    setSaving(true);
    try {
      const body = { name: name.trim(), stages };
      if (pipeline) {
        await api.patch(`/pipelines/${pipeline.id}`, body);
        toast.success("Pipeline updated");
        onSaved();
      } else {
        const res = await api.post<{ pipeline: Pipeline }>("/pipelines", body);
        toast.success("Pipeline created");
        onSaved(res.pipeline.id);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  const onDelete = async () => {
    if (!pipeline) return;
    if (!(await confirm({
      title: `Delete "${pipeline.name}"?`,
      description: "Open opportunities in this pipeline will be detached, not deleted.",
      confirmLabel: "Delete pipeline",
      danger: true,
    }))) return;
    try {
      await api.delete(`/pipelines/${pipeline.id}`);
      toast.success("Deleted");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    }
  };

  return (
    <Modal open onClose={onClose} title={pipeline ? "Edit pipeline" : "New pipeline"} size="lg">
      <div className="space-y-4">
        <div>
          <Label required>Pipeline name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sales Pipeline" autoFocus />
        </div>
        <div>
          <Label>Stages</Label>
          <p className="mb-2 text-xs text-zinc-500">Cards move left-to-right through these stages.</p>
          <ul className="space-y-1.5">
            {stages.map((s, idx) => (
              <li key={s.id} className="grid grid-cols-[auto_1fr_auto_auto] gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 p-2 items-center">
                <div className="flex flex-col gap-0.5">
                  <button type="button" onClick={() => moveStage(idx, -1)} disabled={idx === 0} className="text-zinc-500 hover:text-zinc-200 disabled:opacity-30">▲</button>
                  <button type="button" onClick={() => moveStage(idx, 1)} disabled={idx === stages.length - 1} className="text-zinc-500 hover:text-zinc-200 disabled:opacity-30">▼</button>
                </div>
                <Input value={s.name} onChange={(e) => updateStage(idx, { name: e.target.value })} className="h-8 text-sm" />
                <ColorSwatchPicker value={s.color ?? "zinc"} onChange={(c) => updateStage(idx, { color: c })} />
                <button type="button" onClick={() => removeStage(idx)} className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-rose-300" aria-label="Remove">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
          <Button variant="outline" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={addStage} className="mt-2">
            Add stage
          </Button>
        </div>
      </div>
      <ModalFooter>
        {pipeline && <Button variant="ghost" onClick={onDelete} className="mr-auto text-rose-400 hover:bg-rose-500/10">Delete</Button>}
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={!name.trim()}>{pipeline ? "Save changes" : "Create"}</Button>
      </ModalFooter>
    </Modal>
  );
}

function OpportunityDialog({
  open, onClose, pipeline, opportunity, onSaved,
}: {
  open: boolean;
  onClose: () => void;
  pipeline: Pipeline | null;
  opportunity?: Opportunity;
  onSaved: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [title, setTitle] = useState("");
  const [stageId, setStageId] = useState("");
  const [valueDollars, setValueDollars] = useState("");
  // The selected contact, if any. Tracks both id + display name so we can
  // render the chip even before re-fetching, and so users can clear the
  // selection cleanly (the old fallback-to-opportunity?.contact_name made
  // backspace appear to "snap back" — that's the bug we're fixing here).
  const [selectedContact, setSelectedContact] = useState<{ id: string; name: string } | null>(null);
  const [contactQuery, setContactQuery] = useState("");
  const [contactOptions, setContactOptions] = useState<Contact[]>([]);
  const [status, setStatus] = useState<"open" | "won" | "lost">("open");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (opportunity) {
        setTitle(opportunity.title);
        setStageId(opportunity.stage_id);
        setValueDollars((opportunity.value_cents / 100).toString());
        setSelectedContact(opportunity.contact_id
          ? { id: opportunity.contact_id, name: opportunity.contact_name ?? "—" }
          : null,
        );
        setStatus(opportunity.status);
      } else {
        setTitle("");
        setStageId(pipeline?.stages[0]?.id ?? "");
        setValueDollars("");
        setSelectedContact(null);
        setStatus("open");
      }
      setContactQuery("");
      setContactOptions([]);
    }
  }, [open, opportunity, pipeline]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!contactQuery.trim()) { setContactOptions([]); return; }
      api.get<{ contacts: Contact[] }>("/contacts", { q: contactQuery, limit: 10 })
        .then((d) => setContactOptions(d.contacts))
        .catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [contactQuery]);

  const submit = async () => {
    if (!title.trim() || !pipeline) return;
    setSaving(true);
    const cents = Math.round(parseFloat(valueDollars || "0") * 100) || 0;
    const body = {
      pipeline_id: pipeline.id,
      stage_id: stageId,
      title: title.trim(),
      value_cents: cents,
      contact_id: selectedContact?.id ?? null,
      status,
    };
    try {
      if (opportunity) await api.patch(`/opportunities/${opportunity.id}`, body);
      else await api.post("/opportunities", body);
      toast.success(opportunity ? "Updated" : "Created");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  const onDelete = async () => {
    if (!opportunity) return;
    if (!(await confirm({
      title: "Delete this deal?",
      description: opportunity.title ? `"${opportunity.title}" will be removed.` : undefined,
      confirmLabel: "Delete deal",
      danger: true,
    }))) return;
    try {
      await api.delete(`/opportunities/${opportunity.id}`);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    }
  };

  if (!pipeline) return null;
  return (
    <Modal open={open} onClose={onClose} title={opportunity ? "Edit deal" : "New deal"} size="md">
      <div className="space-y-3">
        <div><Label required>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Acme – Q3 expansion" autoFocus /></div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label>Stage</Label>
            <Select value={stageId} onChange={(e) => setStageId(e.target.value)}>
              {pipeline.stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </div>
          <div>
            <Label>Value (USD)</Label>
            <Input type="number" value={valueDollars} onChange={(e) => setValueDollars(e.target.value)} placeholder="0" leftIcon={<DollarSign className="h-3.5 w-3.5" />} />
          </div>
        </div>
        <div>
          <Label>Contact</Label>
          {selectedContact ? (
            // Chip view — shows the linked contact, clickable to open their
            // record, with an X to clear. Replaces the search input that
            // couldn't actually be cleared all the way.
            <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/50 px-2 py-1.5">
              <Avatar name={selectedContact.name} size="xs" />
              <span className="text-sm text-zinc-200 flex-1 truncate">{selectedContact.name}</span>
              <Link
                to={`/contacts/${selectedContact.id}`}
                state={{ from: "/pipelines" }}
                onClick={onClose}
                title="Open contact record"
                className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-rose-300"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
              <button
                type="button"
                onClick={() => { setSelectedContact(null); setContactQuery(""); }}
                title="Remove contact from this deal"
                aria-label="Remove contact"
                className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-rose-300"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <>
              <Input
                value={contactQuery}
                onChange={(e) => setContactQuery(e.target.value)}
                placeholder="Search contact…"
                leftIcon={<User className="h-3.5 w-3.5" />}
              />
              {contactOptions.length > 0 && (
                <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950">
                  {contactOptions.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setSelectedContact({ id: c.id, name: fullName(c) || c.email || "Unnamed" });
                        setContactQuery("");
                        setContactOptions([]);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-900"
                    >
                      <Avatar name={fullName(c)} email={c.email} size="xs" />
                      <span className="truncate text-zinc-200">{fullName(c)}</span>
                      <span className="ml-auto truncate text-xs text-zinc-500">{c.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        {opportunity && (
          <div>
            <Label>Status</Label>
            <Select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
              <option value="open">Open</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </Select>
          </div>
        )}
      </div>
      <ModalFooter>
        {opportunity && <Button variant="ghost" onClick={onDelete} className="mr-auto text-rose-400 hover:bg-rose-500/10">Delete</Button>}
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={!title.trim()}>{opportunity ? "Save" : "Create"}</Button>
      </ModalFooter>
    </Modal>
  );
}

